import { CombinedGraphQLErrors, ServerError, ServerParseError } from '@apollo/client/errors'
import type { ErrorLink } from '@apollo/client/link/error'

import { notifyHttpError, resolveErrorKey, type HttpErrorInput } from '@libs/toast'
import { captureError } from '@libs/sentry/sentry'

const toHttpErrorInput = (error: unknown): HttpErrorInput => {
  if (ServerError.is(error) || ServerParseError.is(error)) {
    return { status: error.statusCode }
  }

  if (CombinedGraphQLErrors.is(error)) {
    const extensions = error.errors[0]?.extensions
    // Nest reports a 429 as INTERNAL_SERVER_ERROR and keeps the HTTP status alongside.
    if (extensions?.status === 429) return { status: 429 }

    const code = extensions?.code
    return { gqlCode: typeof code === 'string' ? code : undefined }
  }

  return {}
}

export const handleApolloError = (error: unknown): void => {
  const input = toHttpErrorInput(error)
  const key = resolveErrorKey(input)

  // resolveErrorKey returns null only for errors the app handles silently (401 / UNAUTHENTICATED).
  // A rate-limit refusal is deliberate and logged by the API; anything else surfaced is worth reporting to Sentry.
  if (key !== null && key !== 'rate_limit') {
    captureError(error)
  }

  notifyHttpError(input)
}

const isForbidden = (error: unknown): boolean =>
  CombinedGraphQLErrors.is(error) && error.errors.every(({ extensions }) => extensions?.code === 'FORBIDDEN')

// `context: { silentForbidden: true }` keeps a refusal the operation renders itself out of the toast and Sentry.
export const handleOperationError = ({ error, operation }: ErrorLink.ErrorHandlerOptions): void => {
  if (operation.getContext().silentForbidden && isForbidden(error)) return

  handleApolloError(error)
}
